import {EventEmitter, Injectable} from '@angular/core';
import {ElectronService} from 'ngx-electron';
import {LogSource} from '../data/logSource';
import {LogLevel} from '../data/logLevel';
import {LogSourceType} from '../data/logSourceType';
import {InitializeClientLogRequest, Log} from '../data/logFile';
import {ApiService} from '../../app-common/services/server/api.service';
import {LogMessage} from '../data/logMessage';
import {LogObject} from '../data/logObject';
import {AppService} from '../../app-common/services/app/app.service';


@Injectable({
  providedIn: 'root'
})
export class LoggerService {

  constructor(public electronService: ElectronService,
              private appService: AppService) {
    this.electronService = electronService;
    this.appService.messageEvent.subscribe((message) => {
      this.logMessage(LogLevel.INFO, LogSourceType.COMPONENT, LogSource.AppService, `Showing ${message.severity} message: ${message.detail}`);
    });
    this.logMessage(LogLevel.INFO, LogSourceType.SERVICE, LogSource.LoggingService, 'LoggerService started');
  }

  static enabled = true;
  static batchSize: number = 25;

  private messages: LogMessage[] = [];
  private errorTimeout: any = null;
  private rollingWindow: { message: LogMessage; timestamp: number }[] = [];
  private rollingWindowSize = 100; // Adjust this based on your needs
  private lastErrorBurstTimestamp: Date = new Date();
  private debounceTimeout: any = null;

  async sendMessages() {
    if (!LoggerService.enabled) {
      return;
    }
    if (this.messages.length > 0) {
      const logMessages = [];
      for (const message of this.messages) {
        const newLogMessage = new LogMessage(message.level, message.sourceType, message.source, message.message, message.content);
        newLogMessage.time = message.time;
        logMessages.push(newLogMessage);
      }
      this.messages = [];
      if (this.electronService.isElectronApp) {
        this.electronService.ipcRenderer.send('log', {messages: logMessages});
      }
    }
  }

  private createAndPushMessage(level: LogLevel, sourceType: LogSourceType, source: LogSource, content: string | LogObject) {
    let message;
    if (typeof content === 'string' || content instanceof String) {
      message = new LogMessage(level, sourceType, LogSource[source], content as string);
    } else {
      message = new LogMessage(level, sourceType, LogSource[source], content.message, content.entity);
    }
    message.hash = this.getStringHash(message.message);
    let hashMatchesInLastSecond = 0;
    for (const existingMessage of this.messages) {
      if (existingMessage.hash === message.hash && existingMessage.time.getTime() > Date.now() - 1000) {
        hashMatchesInLastSecond++;
      }
    }
    if (hashMatchesInLastSecond > 5) {
      return;
    }
    this.messages.push(message);
    return message;
  }

  private getStringHash(str: string) {
    // tslint:disable-next-line:one-variable-per-declaration
    let hash = 0,
      i, chr;
    if (str.length === 0) { return hash; }
    for (i = 0; i < str.length; i++) {
      chr = str.charCodeAt(i);
      // tslint:disable-next-line:no-bitwise
      hash = ((hash << 5) - hash) + chr;
      // tslint:disable-next-line:no-bitwise
      hash |= 0; // Convert to 32bit integer
    }
    return hash;
  }

  private debounceSendMessages(level: LogLevel, delay: number) {
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }
    this.debounceTimeout = setTimeout(async () => {
      await this.sendMessages();
    }, delay);
  }

  async logMessage(level: LogLevel, sourceType: LogSourceType, source: LogSource, content: string | LogObject) {
    const newEntry = this.createAndPushMessage(level, sourceType, source, content);

    this.rollingWindow.push({message: newEntry, timestamp: Date.now()});
    if (this.rollingWindow.length > this.rollingWindowSize) {
      this.rollingWindow.shift(); // Remove the oldest message
    }

    // Detecting an error burst
    if (level === LogLevel.ERROR) {
      if (this.isErrorBurst(10, 1000)) { // Set the threshold and interval based on your needs
        console.warn('A burst of errors detected. Possible infinite loop causing errors.');

        if ((new Date()).getTime() - this.lastErrorBurstTimestamp.getTime() < 1000 * 60) {
          return;
        }
        this.lastErrorBurstTimestamp = new Date();
        // Take appropriate action, e.g., stop logging, show a warning message, or send an alert
        this.appService.showWarnMessage('An issue with the application has been detected.' +
          ' We would suggest you to restart the application when possible.' +
          ' Please contact support@godh.com if you would like to get in touch with our support team.');
      }
    }
    if (this.messages.length >= LoggerService.batchSize || level === LogLevel.ERROR) {
      const delay = level === LogLevel.ERROR ? 100 : 0;
      this.debounceSendMessages(level, delay);
    }
  }

  private isErrorBurst(threshold: number, interval: number): boolean
    {
      const errorMessages = this.rollingWindow.filter(entry => entry.message && entry.message.level === LogLevel.ERROR);
      let burstCount = 0;

      for (let i = 1; i < errorMessages.length; i++) {
        if (errorMessages[i].timestamp - errorMessages[i - 1].timestamp <= interval) {
          burstCount++;
        }

        if (burstCount >= threshold - 1) {
          return true;
        }
      }

      return false;
    }
}
